import { addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DOWNLOAD_ANDROID_PATH } from '../constants/appLinks';

export type MarketingUtm = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
};

const MARKETING_ANALYSIS_COLLECTION = 'MarketingAnalysis';
const FIRESTORE_LOG_TIMEOUT_MS = 900;

export function parseUtmParams(search: string): MarketingUtm {
  const params = new URLSearchParams(search);
  return {
    source: params.get('utm_source') ?? undefined,
    medium: params.get('utm_medium') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
    content: params.get('utm_content') ?? undefined,
    term: params.get('utm_term') ?? undefined,
  };
}

function deriveTrafficSource(utm: MarketingUtm, referrer: string | null): string {
  if (utm.source) {
    if (utm.source === 'facebook' || utm.source === 'instagram') {
      return `paid_${utm.source}`;
    }
    if (utm.source === 'google') return 'paid_google';
    return `utm_${utm.source}`;
  }
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (host.includes('facebook.com') || host.includes('fb.com')) return 'social_facebook';
    if (host.includes('instagram.com')) return 'social_instagram';
    if (host.includes('google.')) return 'organic_google';
  } catch {
    /* ignore */
  }
  return 'external_referrer';
}

function buildSessionKey(utm: MarketingUtm): string {
  const campaign = utm.campaign ?? 'none';
  return `${Date.now()}_${campaign}`.slice(0, 120);
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Logs redirect hit to Firestore MarketingAnalysis (anonymous create allowed by rules).
 * Never throws. Times out so Play handoff is not blocked.
 */
export async function logDownloadAndroidRedirect(search: string): Promise<void> {
  try {
    if (typeof window === 'undefined') return;

    const utm = parseUtmParams(search);
    const sessionKey = buildSessionKey(utm);
    const playStoreReferrer = new URLSearchParams(search).toString().slice(0, 500);
    const referrer =
      typeof document !== 'undefined' && document.referrer ? document.referrer : null;

    const record = {
      recordType: 'redirect' as const,
      landingPath: DOWNLOAD_ANDROID_PATH,
      timestamp: new Date().toISOString(),
      utm: {
        ...(utm.source ? { source: utm.source } : {}),
        ...(utm.medium ? { medium: utm.medium } : {}),
        ...(utm.campaign ? { campaign: utm.campaign } : {}),
        ...(utm.content ? { content: utm.content } : {}),
        ...(utm.term ? { term: utm.term } : {}),
      },
      referrer,
      userAgent:
        typeof navigator !== 'undefined' && navigator.userAgent
          ? navigator.userAgent.slice(0, 480)
          : 'unknown',
      language:
        typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'ar',
      trafficSource: deriveTrafficSource(utm, referrer),
      playStoreReferrer,
      sessionKey,
    };

    await withTimeout(
      addDoc(collection(db, MARKETING_ANALYSIS_COLLECTION), record),
      FIRESTORE_LOG_TIMEOUT_MS,
    );
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[morafaa] download-android MarketingAnalysis log failed (silent):', error);
    }
  }
}
